/**
 * F4 — the module manifest loader.
 *
 * One declarative table (`configs/business-types/modules.json`) replaces the three
 * things that used to answer "what does this business get": per-type surface
 * visibility, per-surface reveal triggers, and per-sub-type overrides. PROP-00 §4
 * is the brief; PROP-02 §3 is the table.
 *
 * ── Why this is a loader and not an enum ──────────────────────────────────────
 * The manifest's key space is NOT free. Every key is a `manifestKey` already
 * declared by `lib/routing/route-table.ts` — a section root is the section name,
 * a sub-tab is `section.tab`. Two tables that must agree, written by two tasks in
 * two phases, are exactly the kind of pair that drifts, so this module publishes
 * the keys and the test asserts the agreement in both directions. That test is
 * the `check:module-manifest` guard from PROP-02 F4.
 *
 * This module deliberately does NOT import the route table. `lib/config-system` is
 * the leaf every other layer reads; a loader under it that reached up into
 * `lib/routing` would invert the dependency and put a routing table inside the
 * client bundle of anything that only wanted the manifest.
 *
 * ── The three states ─────────────────────────────────────────────────────────
 *   default = on, as soon as its parent workspace is on
 *   offered = listed in Add features; the owner switches it on by hand
 *   hidden  = never shown for this sub-type
 *
 * `default` on a sub-tab whose parent is `offered` is deliberate. PROP-00 §4:
 * "a food truck that enables Customers gets Customers · Loyalty · Promotions;
 * Gift cards stays offered." The parent decides whether the tab is *reachable*;
 * the tab's own state decides whether it *arrives free* or has to be asked for.
 * Only `hidden` propagates downward.
 *
 * ── The design law is unchanged ───────────────────────────────────────────────
 * Doc 33: a business type authorizes NOTHING. A manifest row can hide a module
 * the plan includes; it can never reveal one the plan excludes. The owner
 * switches are the only thing that turns `offered` into `default`, and they are
 * passed IN by the caller (`branch_capability_packs` stores them, P5 owns that
 * table). Nothing in this module reads a database, a session or a plan, so it is
 * safe to call from a client component.
 *
 * @location lib/config-system/module-manifest.ts
 */

import manifestConfig from '@/configs/business-types/modules.json';
import businessTypesConfig from '@/configs/business-types/types.json';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A module's state in the manifest. See the three-states note above. */
export type ModuleState = 'default' | 'offered' | 'hidden';

/** The lane `/pos/[branchId]` opens, or `none` for a sub-type with no POS. */
export type PosEntryLane = 'till' | 'floor' | 'none';

export interface SubTypeManifest {
  readonly posEntry: PosEntryLane;
  readonly modules: Readonly<Record<string, ModuleState>>;
}

export interface ModuleManifestConfig {
  readonly states: readonly ModuleState[];
  readonly alwaysOn: readonly string[];
  readonly workspaceSections: readonly string[];
  readonly alwaysOnSections: readonly string[];
  readonly posLanes: readonly string[];
  readonly subTypes: Readonly<Record<string, Readonly<Record<string, SubTypeManifest>>>>;
}

// ─── The loaded config ────────────────────────────────────────────────────────

const config = manifestConfig as unknown as ModuleManifestConfig;

export const MODULE_STATES: readonly ModuleState[] = config.states;

/** The keys `default` for every sub-type (PROP-00 §4's always-on sections). */
export const ALWAYS_ON_KEYS: readonly string[] = config.alwaysOn;

/** The five workspaces that exist only when a sub-type needs them. */
export const WORKSPACE_KEYS: readonly string[] = config.workspaceSections;

/** The eight sections on for every business type. */
export const ALWAYS_ON_SECTION_KEYS: readonly string[] = config.alwaysOnSections;

/** `Till · Floor · Kitchen · Ready · Orders` (PROP-02 §4). */
export const POS_LANE_KEYS: readonly string[] = config.posLanes;

/** The route that redirects into a POS lane. Not a lane, so not in `POS_LANE_KEYS`. */
export const POS_ENTRY_KEY = 'pos.entry';

/** The POS section. It has no `manifestKey` of its own — see `parentKeyOf`. */
export const POS_SECTION = 'pos';

const SUB_TYPES = config.subTypes;

/**
 * `branches.specific_type` stores the sub-type's **label** (`subTypes.<key>.label`,
 * e.g. `"Fast Food"`), not its config key — the same thing `getSubType` in the
 * business-type registry matches on, and what every other resolver in the app
 * expects. The manifest, however, is keyed by the config KEY (`fast_food`).
 *
 * This index bridges the two. Without it the manifest looked up `"Fast Food"`,
 * found no row, and fell back to the conservative `plainModules()` answer — which
 * never hides anything, so a `standard` branch with a sub-type still showed the
 * **Catering** workspace the sub-type's own row marks `hidden`.
 */
const SUB_TYPE_KEY_BY_LABEL: ReadonlyMap<string, { typeId: string; key: string }> = (() => {
  const index = new Map<string, { typeId: string; key: string }>();
  const types = (businessTypesConfig as { businessTypes?: Record<string, { subTypes?: Record<string, { label?: string }> }> })
    .businessTypes ?? {};
  for (const [typeId, typeCfg] of Object.entries(types)) {
    for (const [key, sub] of Object.entries(typeCfg.subTypes ?? {})) {
      if (sub?.label) index.set(sub.label, { typeId, key });
    }
  }
  return index;
})();

/**
 * Normalize a stored `specific_type` to the manifest's config key. Accepts either
 * the label (`"Fast Food"`) or a key (`"fast_food"`) so a caller that already has
 * the key keeps working, and returns null when nothing matches — the honest
 * "unknown sub-type" the fallback is designed for.
 *
 * When `businessType` is supplied, a LABEL resolves only to a row owned by that
 * type. That is the doc 34/48 law: a `specific_type` left behind by a business-type
 * change (`"Wine Bar"` on a now-`standard` branch) must inherit silently, not
 * apply the old type's row to the new type. A bare KEY is still returned as-is —
 * keys are type-unambiguous in the table, and legacy callers pass them without a
 * type.
 */
function manifestSubTypeKey(
  subType: string | null | undefined,
  businessType?: string | null,
): string | null {
  if (typeof subType !== 'string' || subType.length === 0) return null;
  const byLabel = SUB_TYPE_KEY_BY_LABEL.get(subType);
  if (byLabel) {
    if (businessType && byLabel.typeId !== businessType) return null;
    return byLabel.key;
  }
  return subType;
}

/** Every sub-type id in the manifest, grouped by parent business type. */
export const MANIFEST_SUB_TYPES: Readonly<Record<string, readonly string[]>> = Object.freeze(
  Object.fromEntries(
    Object.entries(SUB_TYPES).map(([typeId, ids]) => [typeId, Object.freeze(Object.keys(ids))]),
  ),
);

/** Flat list of every sub-type id in the manifest, across all business types. */
export const ALL_SUB_TYPE_IDS: readonly string[] = Object.values(SUB_TYPES).flatMap((ids) =>
  Object.keys(ids),
);

/** The complete key space: always-on keys plus every key any sub-type names. */
export const MANIFEST_KEYS: readonly string[] = Object.freeze([
  ...new Set([...ALWAYS_ON_KEYS, ...Object.values(SUB_TYPES).flatMap((ids) => Object.values(ids).flatMap((b) => Object.keys(b.modules)))]),
].sort());

const KEY_SET = new Set(MANIFEST_KEYS);

// ─── Sub-type lookup ──────────────────────────────────────────────────────────

/** Is this a sub-type the manifest has a row for? Accepts the stored label or key. */
export function isManifestSubType(subType: string | null | undefined): boolean {
  const key = manifestSubTypeKey(subType);
  return key !== null && ALL_SUB_TYPE_IDS.includes(key);
}

/**
 * The parent business type id owning a sub-type, or null when the manifest has
 * no row. Lets a caller confirm the sub-type belongs to the branch's type before
 * applying it — a stale `specific_type` left behind by a type change must never
 * keep modifying the new type. Accepts the stored label or key.
 */
export function businessTypeForSubType(subType: string | null | undefined): string | null {
  const key = manifestSubTypeKey(subType);
  if (key === null) return null;
  for (const [typeId, ids] of Object.entries(SUB_TYPES)) {
    if (ids[key]) return typeId;
  }
  return null;
}

/** One sub-type's row, or null when the manifest has none. Accepts label or key. */
export function getSubTypeManifest(
  subType: string | null | undefined,
  businessType?: string | null,
): SubTypeManifest | null {
  const key = manifestSubTypeKey(subType, businessType);
  if (key === null) return null;
  for (const ids of Object.values(SUB_TYPES)) {
    if (ids[key]) return ids[key];
  }
  return null;
}

/** The POS entry lane for a sub-type, or `none` when it has no manifest row. */
export function posEntryFor(
  subType: string | null | undefined,
  businessType?: string | null,
): PosEntryLane {
  return getSubTypeManifest(subType, businessType)?.posEntry ?? 'none';
}

// ─── Resolution ───────────────────────────────────────────────────────────────

/**
 * The state of every key for a branch with no usable sub-type — no
 * `specific_type`, a label the manifest does not know, or a label left behind by
 * a type change.
 *
 * Doc 34/48 requires that a stale label inherits silently rather than keep
 * modifying a type it no longer belongs to, and doc 33 requires that a type
 * grants nothing. Both point the same way: an unknown sub-type must neither force
 * a module on nor take one away. Every conditional module is `offered`, so the
 * owner decides, and the always-on keys stay `default`.
 *
 * NOTE this is the answer for a genuinely unknown sub-type only. A KNOWN stored
 * label resolves through `SUB_TYPE_KEY_BY_LABEL` to its row first — a `"Fast
 * Food"` branch is not "unknown", and must get its row (which hides Catering),
 * not this lenient fallback.
 *
 * The workspace sub-tabs are `default` rather than `offered` for the same reason
 * the doc's food-truck example is: a tab that arrives with its workspace is the
 * norm, and the rare tab that must be asked for by hand is marked `offered` in
 * the row.
 */
function plainModules(): Record<string, ModuleState> {
  const out: Record<string, ModuleState> = {};
  for (const key of MANIFEST_KEYS) {
    if (ALWAYS_ON_KEYS.includes(key)) {
      out[key] = 'default';
    } else if (WORKSPACE_KEYS.includes(key.split('.')[0])) {
      out[key] = key.split('.').length > 1 ? 'default' : 'offered';
    } else {
      out[key] = 'offered';
    }
  }
  return out;
}

const PLAIN = Object.freeze(plainModules());

/**
 * The full manifest for a sub-type: all `MANIFEST_KEYS`, always-on first, then
 * the sub-type's own row. Never partial, so a caller can index it without
 * guessing whether a missing key means "hidden" or "not configured".
 */
export function resolveModules(
  subType: string | null | undefined,
  businessType?: string | null,
): Record<string, ModuleState> {
  const row = getSubTypeManifest(subType, businessType);
  if (!row) return { ...PLAIN };
  const out: Record<string, ModuleState> = {};
  for (const key of MANIFEST_KEYS) {
    out[key] = row.modules[key] ?? (ALWAYS_ON_KEYS.includes(key) ? 'default' : 'offered');
  }
  return out;
}

// ─── Questions callers actually ask ──────────────────────────────────────────

/**
 * The key that gates this one, or null when nothing does.
 *
 * `pos.*` has no gate on purpose: POS availability comes from the plan's POS
 * addon, not from a business type, and the manifest's POS entries are lane
 * presentation inside a POS the branch already has.
 */
export function parentKeyOf(key: string): string | null {
  const dot = key.indexOf('.');
  if (dot < 0) return null;
  const parent = key.slice(0, dot);
  return KEY_SET.has(parent) ? parent : null;
}

/** May this key be shown at all for this sub-type? `hidden` is the only refusal. */
export function isModuleAvailable(
  key: string,
  subType?: string | null,
  businessType?: string | null,
): boolean {
  return resolveModules(subType, businessType)[key] !== 'hidden';
}

/** The manifest state of a key, for a switch screen that has to name the state. */
export function moduleState(
  key: string,
  subType?: string | null,
  businessType?: string | null,
): ModuleState {
  return resolveModules(subType, businessType)[key] ?? 'hidden';
}

/**
 * Is this module on for this branch, given what the owner has switched on?
 *
 * A module is on when it is not `hidden`, its parent is on, and either it is
 * `default` or the owner has switched it on individually. `switchedOn` is the
 * owner's set of switches and is supplied by the caller — P5 stores it in
 * `branch_capability_packs`.
 *
 * Pass an empty set for a branch that has never opened the switch screen, which
 * is the correct answer for every `default` module and the conservative answer
 * for every `offered` one.
 */
export function isModuleOn(
  key: string,
  subType?: string | null | undefined,
  switchedOn?: ReadonlySet<string> | null,
  businessType?: string | null,
): boolean {
  const states = resolveModules(subType, businessType);
  const state = states[key];
  if (state === undefined || state === 'hidden') return false;
  const parent = parentKeyOf(key);
  if (parent && !isModuleOn(parent, subType, switchedOn, businessType)) return false;
  if (state === 'default') return true;
  return switchedOn?.has(key) ?? false;
}

/**
 * The keys an owner could switch on for this sub-type: every `offered` module
 * whose ancestors are available. This is the input for the Settings → Plan &
 * features screen (PROP-02 F4) and for deciding what `branch_capability_packs`
 * needs to store.
 */
export function offeredModules(
  subType?: string | null | undefined,
  switchedOn?: ReadonlySet<string> | null,
  businessType?: string | null,
): string[] {
  const states = resolveModules(subType, businessType);
  return MANIFEST_KEYS.filter((key) => {
    if (states[key] !== 'offered') return false;
    const parent = parentKeyOf(key);
    return !parent || isModuleAvailable(parent, subType, businessType);
  });
}

/** Every module on for this sub-type, ancestors included. */
export function enabledModules(
  subType?: string | null | undefined,
  switchedOn?: ReadonlySet<string> | null,
  businessType?: string | null,
): string[] {
  return MANIFEST_KEYS.filter((key) => isModuleOn(key, subType, switchedOn, businessType));
}
